import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Mail, X } from 'lucide-react';
import { catalogueApi } from '../services/api';
import { VariantProductDetail } from '../components/catalogue/VariantProductDetail';

const SPEC_ORDER = [
  'wattage', 'lumens', 'color_temp', 'voltage', 'control', 'cri',
  'ip_rating', 'Dimensions', 'Mounting', 'Certification', 'Numbers', 'Font', 'Lamping'
];

const SPEC_LABELS = {
  wattage: 'Wattage',
  lumens: 'Lumens',
  color_temp: 'Colour Temperature',
  voltage: 'Voltage',
  control: 'Control',
  cri: 'CRI',
  ip_rating: 'IP Rating',
  Dimensions: 'Dimensions',
  Mounting: 'Mounting',
  Certification: 'Certification',
  Numbers: 'Numbers',
  Font: 'Font',
  Lamping: 'Lamping',
};

const INFERRED_SPECS = [
  { key: 'Beam Angle', value: 'Diffuse / 180\u00B0' },
  { key: 'Materials', value: 'Solid metal frame, opal acrylic or glass diffuser' },
  { key: 'Finish', value: 'Hand-applied painted or plated finish (customisable)' },
];

const CHIP_KEYS = ['wattage', 'color_temp', 'voltage', 'control', 'cri'];

const ProductDetailPage = () => {
  const { brandId, slug } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Get the catalogue page from URL search params or location state
  const searchParams = new URLSearchParams(location.search);
  const fromPage = searchParams.get('page') || '1';

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await catalogueApi.get(brandId, slug);
        setData(res.data);
        setSelectedImage(0);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [brandId, slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data?.product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-medium text-gray-900 mb-4">Product not found</h1>
          <Link to={`/brand/${brandId}`} className="text-gray-600 hover:text-gray-900">
            Return to Brand
          </Link>
        </div>
      </div>
    );
  }

  if (data.product.variants?.length) {
    return <VariantProductDetail brandId={brandId} data={data} />;
  }

  const { product, index, total, prev, next } = data;
  const images = product.images?.length > 0 ? product.images : (product.mainImage ? [product.mainImage] : []);
  const specs = product.specs || {};
  const chips = CHIP_KEYS.map(k => specs[k]).filter(Boolean);

  // Clean description — strip Numera contact footer
  const cleanDescription = (product.description || '')
    .replace(/Contact us at 410-298-8100[\s\S]*$/i, '')
    .replace(/\nDimensions[\s\S]*$/i, '') // Strip spec block from desc if embedded
    .trim();

  // Build mailto
  const mailtoSubject = encodeURIComponent('Numera enquiry');
  const mailtoBody = encodeURIComponent(
    `I would like to enquire about:\n\nProduct: ${product.code} - "${product.name}"\nPage: ${window.location.href}\n\nPlease provide specification and pricing details.`
  );
  const mailtoHref = `mailto:info@meshlighting.co.uk?subject=${mailtoSubject}&body=${mailtoBody}`;

  // Build ordered spec rows
  const specRows = [];
  SPEC_ORDER.forEach(key => {
    if (specs[key]) {
      specRows.push({ label: SPEC_LABELS[key] || key, value: specs[key] });
    }
  });
  INFERRED_SPECS.forEach(s => {
    specRows.push({ label: s.key, value: s.value });
  });

  return (
    <div className="bg-white" data-testid="product-detail-page">
      {/* Top Bar */}
      <div className="border-b bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link
            to={`/brand/${brandId}?catPage=${fromPage}`}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="back-to-catalogue"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {product.brandId === 'numera' ? 'Numera' : 'Brand'} Catalogue
          </Link>

          <span className="text-sm text-gray-500 font-mono" data-testid="product-counter">
            {index} / {total}
          </span>

          <div className="flex items-center gap-3">
            {prev && (
              <Link
                to={`/brand/${brandId}/products/${prev.slug}?page=${fromPage}`}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                data-testid="prev-product"
              >
                <ChevronLeft className="h-4 w-4" />
                {prev.code}
              </Link>
            )}
            {next && (
              <Link
                to={`/brand/${brandId}/products/${next.slug}?page=${fromPage}`}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                data-testid="next-product"
              >
                {next.code}
                <ChevronRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Gallery — left side */}
          <div className="lg:col-span-7">
            {/* Main image */}
            <div
              className="aspect-[5/4] bg-gray-50 rounded-lg overflow-hidden cursor-pointer mb-4"
              style={{ maxHeight: '520px' }}
              onClick={() => setLightboxOpen(true)}
              data-testid="main-product-image"
            >
              {images[selectedImage] && (
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              )}
            </div>

            {/* Thumbnail strip */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2" data-testid="thumbnail-strip">
                {images.map((img, i) => (
                  <button
                    key={img}
                    onClick={() => setSelectedImage(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-colors ${
                      i === selectedImage ? 'border-gray-900' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain bg-gray-50" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Meta column — right side */}
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">
              {(product.brandId || '').toUpperCase()} &middot; {product.category}
            </p>
            <p className="text-sm font-mono text-gray-500 mb-1" data-testid="product-code">{product.code}</p>
            <h1 className="text-3xl md:text-4xl font-light text-gray-900 mb-4" data-testid="product-name">
              "{product.name}"
            </h1>

            {cleanDescription && (
              <p className="text-gray-600 leading-relaxed mb-6 line-clamp-4">
                {cleanDescription.split('\n')[0]}
              </p>
            )}

            {/* Chips */}
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8" data-testid="spec-chips">
                {chips.map((chip) => (
                  <span
                    key={chip}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-full"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            )}

            {/* Enquiry CTA */}
            <a
              href={mailtoHref}
              className="inline-flex items-center w-full justify-center px-6 py-4 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 transition-colors"
              data-testid="enquiry-button"
            >
              <Mail className="h-4 w-4 mr-2" />
              Enquire about {product.code}
            </a>
          </div>
        </div>

        {/* Technical Specifications */}
        <section className="mt-16 bg-gray-50 rounded-lg p-8" data-testid="specs-section">
          <h2 className="text-xl font-medium text-gray-900 mb-6">Technical Specifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            {specRows.map((row) => (
              <div key={row.label} className="flex justify-between py-3 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-500">{row.label}</span>
                <span className="text-sm text-gray-900 text-right max-w-[60%]">{row.value}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Full Description */}
        {cleanDescription && (
          <section className="mt-12" data-testid="description-section">
            <h2 className="text-xl font-medium text-gray-900 mb-4">Description</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">{cleanDescription.split('\n')[0]}</p>
          </section>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          data-testid="lightbox"
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white/70 hover:text-white"
          >
            <X className="h-8 w-8" />
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(i => (i - 1 + images.length) % images.length); }}
                className="absolute left-4 text-white/70 hover:text-white p-2"
              >
                <ChevronLeft className="h-10 w-10" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(i => (i + 1) % images.length); }}
                className="absolute right-4 text-white/70 hover:text-white p-2"
              >
                <ChevronRight className="h-10 w-10" />
              </button>
            </>
          )}

          <img
            src={images[selectedImage]}
            alt={product.name}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
